import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  GraduationCap, Leaf, Trophy, Users, BookOpen, Target,
  ChevronRight, Star, Sparkles, PlayCircle, FileText,
  Calendar, Award, CheckCircle, ArrowRight, School,
  Globe, Palette, TreePine, MessageCircle, Play
} from "lucide-react";
import ClassroomDemo from "../components/ClassroomDemo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EducatorPage = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [stats, setStats] = useState({ educators: 0, challenges: 0, students: 0 });
  const [showDemo, setShowDemo] = useState(false);

  useEffect(() => {
    // Fetch some stats
    fetch(`${BACKEND_URL}/api/challenges/stats`)
      .then(res => res.json())
      .then(data => setStats(data))
      .catch(() => {});
  }, []);

  const features = [
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: "Growth Tool",
      description: "AI-powered lesson plan generator. Enter any standard and topic, get a complete SAE 5E lesson plan instantly.",
      link: "/growth-tool",
      color: "from-violet-500 to-purple-600",
      featured: true
    },
    {
      icon: <Trophy className="w-8 h-8" />,
      title: "GROWTH Challenges",
      description: "Create custom gardening challenges aligned with educational standards. Track student progress and award certificates.",
      link: "/challenges",
      color: "from-amber-500 to-orange-500"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Class Management",
      description: "Generate class codes for private challenges. Monitor student submissions and progress in real-time.",
      link: "/teacher",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: <BookOpen className="w-8 h-8" />,
      title: "Standards Alignment",
      description: "Challenges aligned with NGSS, USVI ELA & Math standards. Perfect for science, math, and environmental education.",
      link: "/teacher",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Career Certificates",
      description: "Students earn career-level certificates: Seedling Scout, Garden Apprentice, Master Cultivator, and more.",
      link: "/challenges",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: <Palette className="w-8 h-8" />,
      title: "Design Studio",
      description: "Visual garden planning tool where students can design, plan, and render their dream gardens.",
      link: "/design",
      color: "from-rose-500 to-red-500"
    },
    {
      icon: <TreePine className="w-8 h-8" />,
      title: "Plant Database",
      description: "Comprehensive database with growing guides, care tips, and harvest calendars for hundreds of plants.",
      link: "/plants",
      color: "from-teal-500 to-green-500"
    }
  ];

  const benefits = [
    "Free Master Gardener access for verified educators",
    "Create unlimited class challenges",
    "Track individual student progress",
    "Generate printable certificates",
    "Access to 5E lesson plan generator",
    "Standards-aligned curriculum resources",
    "Private class codes for secure access",
    "Export student progress reports"
  ];

  const domains = [
    { domain: ".edu", description: "US Educational Institutions" },
    { domain: "vide.vi", description: "US Virgin Islands Dept. of Education" },
    { domain: ".ac.uk", description: "UK Academic Institutions" },
    { domain: ".edu.au", description: "Australian Educational Institutions" },
    { domain: ".k12.*", description: "K-12 School Districts" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-green-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur border-b">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="RootSpace" className="h-8 w-auto" />
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/challenges" className="text-sm font-medium text-muted-foreground hover:text-primary">
              GROWTH
            </Link>
            <Link to="/teacher" className="text-sm font-medium text-muted-foreground hover:text-primary">
              Teacher Dashboard
            </Link>
            {user ? (
              <Button onClick={() => navigate("/teacher")} className="bg-gradient-to-r from-amber-500 to-orange-500">
                <GraduationCap className="w-4 h-4 mr-2" />
                My Dashboard
              </Button>
            ) : (
              <Button onClick={login} className="bg-gradient-to-r from-amber-500 to-orange-500">
                <GraduationCap className="w-4 h-4 mr-2" />
                Educator Sign In
              </Button>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <Badge className="mb-4 bg-amber-100 text-amber-700 border-amber-200">
              <GraduationCap className="w-4 h-4 mr-1" />
              For Educators
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold mb-4">
              <span className="bg-gradient-to-r from-amber-600 via-orange-500 to-green-600 bg-clip-text text-transparent">
                Grow the Next Generation
              </span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              ROOTSPACE provides educators with powerful tools to teach sustainable agriculture, 
              environmental science, and life skills through hands-on garden-based learning.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              {user?.is_educator ? (
                <>
                  <Button 
                    size="lg" 
                    onClick={() => navigate("/teacher")}
                    className="bg-gradient-to-r from-green-600 to-emerald-500"
                  >
                    <Target className="w-5 h-5 mr-2" />
                    Go to Teacher Dashboard
                    <ChevronRight className="w-5 h-5 ml-1" />
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline"
                    onClick={() => setShowDemo(true)}
                  >
                    <Play className="w-5 h-5 mr-2" />
                    Try Classroom Demo
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    size="lg" 
                    onClick={login}
                    className="bg-gradient-to-r from-amber-500 to-orange-500"
                  >
                    <School className="w-5 h-5 mr-2" />
                    Sign In with .edu Email
                    <ChevronRight className="w-5 h-5 ml-1" />
                  </Button>
                  <Button 
                    size="lg" 
                    variant="outline"
                    onClick={() => setShowDemo(true)}
                  >
                    <Play className="w-5 h-5 mr-2" />
                    Try Classroom Demo
                  </Button>
                </>
              )}
            </div>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-3 gap-4 max-w-2xl mx-auto mb-16"
          >
            <Card className="text-center p-4 bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
              <p className="text-3xl font-bold text-amber-600">{stats.educators || "50+"}+</p>
              <p className="text-sm text-muted-foreground">Educators</p>
            </Card>
            <Card className="text-center p-4 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
              <p className="text-3xl font-bold text-green-600">{stats.challenges || "100"}+</p>
              <p className="text-sm text-muted-foreground">Challenges</p>
            </Card>
            <Card className="text-center p-4 bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
              <p className="text-3xl font-bold text-blue-600">{stats.students || "500"}+</p>
              <p className="text-sm text-muted-foreground">Students</p>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4">Educator Tools & Features</h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            Everything you need to bring garden-based learning to your classroom
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card 
                  className="h-full hover:shadow-lg transition-shadow cursor-pointer group"
                  onClick={() => navigate(feature.link)}
                >
                  <CardHeader>
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center text-white mb-3`}>
                      {feature.icon}
                    </div>
                    <CardTitle className="flex items-center justify-between">
                      {feature.title}
                      <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-green-100 text-green-700">Free for Educators</Badge>
              <h2 className="text-3xl font-bold mb-4">
                Full Access for Verified Educators
              </h2>
              <p className="text-muted-foreground mb-6">
                Teachers and educators with verified institutional emails get free Master Gardener 
                access - our highest tier with all premium features included.
              </p>
              
              <ul className="space-y-3">
                {benefits.map((benefit, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <span>{benefit}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
            
            <Card className="p-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-primary" />
                Recognized Educational Domains
              </h3>
              <div className="space-y-3">
                {domains.map((d, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <code className="text-sm font-mono text-primary">{d.domain}</code>
                    <span className="text-sm text-muted-foreground">{d.description}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                Sign in with your institutional email to automatically unlock free premium access.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">How It Works</h2>
          
          <div className="space-y-8">
            {[
              {
                step: "1",
                title: "Sign In with Your .edu Email",
                description: "Use your institutional email to create an account. You'll automatically get free Master Gardener access.",
                icon: <School className="w-6 h-6" />
              },
              {
                step: "2",
                title: "Create a Challenge",
                description: "Design garden-based learning challenges aligned with educational standards. Set goals, timeframes, and success criteria.",
                icon: <Target className="w-6 h-6" />
              },
              {
                step: "3",
                title: "Share Your Class Code",
                description: "Generate a unique 6-character code for your class. Students join with the code to access private challenges.",
                icon: <Users className="w-6 h-6" />
              },
              {
                step: "4",
                title: "Track Progress & Award Certificates",
                description: "Monitor student submissions, track which standards they've demonstrated, and generate printable certificates.",
                icon: <Award className="w-6 h-6" />
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="flex gap-6"
              >
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center text-white font-bold text-lg">
                    {item.step}
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-2 flex items-center gap-2">
                    {item.icon}
                    {item.title}
                  </h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-16 px-4 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-8">Quick Links for Educators</h2>
          
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2 bg-white hover:bg-amber-50"
              onClick={() => navigate("/teacher")}
            >
              <GraduationCap className="w-6 h-6 text-amber-600" />
              <span>Teacher Dashboard</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2 bg-white hover:bg-green-50"
              onClick={() => navigate("/challenges")}
            >
              <Trophy className="w-6 h-6 text-green-600" />
              <span>GROWTH Challenges</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2 bg-white hover:bg-blue-50"
              onClick={() => navigate("/plants")}
            >
              <Leaf className="w-6 h-6 text-blue-600" />
              <span>Plant Database</span>
            </Button>
            <Button 
              variant="outline" 
              className="h-auto py-4 flex-col gap-2 bg-white hover:bg-purple-50"
              onClick={() => navigate("/design")}
            >
              <Palette className="w-6 h-6 text-purple-600" />
              <span>Design Studio</span>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 bg-gradient-to-r from-green-600 to-emerald-500 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-80" />
          <h2 className="text-3xl font-bold mb-4">Ready to Start Growing?</h2>
          <p className="text-white/90 mb-8 max-w-2xl mx-auto">
            Join thousands of educators using ROOTSPACE to teach sustainable agriculture 
            and environmental stewardship through hands-on learning.
          </p>
          
          {user?.is_educator ? (
            <Button 
              size="lg" 
              variant="secondary"
              onClick={() => navigate("/teacher")}
            >
              Go to Teacher Dashboard
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          ) : (
            <Button 
              size="lg" 
              variant="secondary"
              onClick={login}
            >
              Sign In with Your .edu Email
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 bg-gray-900 text-white">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <Leaf className="w-5 h-5" />
            <span className="font-bold">ROOTSPACE</span>
            <span className="text-white/60">for Educators</span>
          </div>
          <div className="flex gap-6 text-sm text-white/60">
            <Link to="/" className="hover:text-white">Home</Link>
            <Link to="/challenges" className="hover:text-white">GROWTH</Link>
            <Link to="/teacher" className="hover:text-white">Teacher Dashboard</Link>
            <Link to="/contact" className="hover:text-white">Contact</Link>
          </div>
          <p className="text-sm text-white/60">© 2026 RootSpace.Pro</p>
        </div>
      </footer>

      {/* Classroom Demo Modal */}
      {showDemo && <ClassroomDemo onClose={() => setShowDemo(false)} />}
    </div>
  );
};

export default EducatorPage;
